import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Mail, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const KEY = "otelpro-newsletter-popup";

export default function NewsletterPopup() {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({
        popup_delay: 30,
        popup_active: true,
        display_frequency: "once",
    });

    useEffect(() => {
        let timer;
        (async () => {
            try {
                const { data } = await api.get("/newsletter/settings/public");
                if (data?.popup_active === false) return;
                setSettings({
                    popup_delay: Number(data?.popup_delay) || 30,
                    popup_active: data?.popup_active !== false,
                    display_frequency: data?.display_frequency || "once",
                });
                if (!shouldShow(data?.display_frequency || "once")) return;
                timer = setTimeout(() => setOpen(true), (Number(data?.popup_delay) || 30) * 1000);
            } catch (_) {}
        })();
        return () => timer && clearTimeout(timer);
    }, []);

    const close = () => {
        setOpen(false);
        markShown(settings.display_frequency);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            const lang = (i18n.language || "tr").split("-")[0];
            await api.post("/newsletter/subscribe", {
                email: email.trim(),
                source: "popup",
                lang,
            });
            toast.success(t("newsletter.title") + " ✓");
            markShown("once");
            setOpen(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="newsletter-popup">
            <div className="absolute inset-0 bg-dark-navy/70 backdrop-blur-sm" onClick={close} />
            <div className="relative w-full max-w-md bg-dark-navy text-white rounded-xl shadow-2xl border border-white/10 overflow-hidden">
                <button
                    onClick={close}
                    aria-label="Close"
                    data-testid="newsletter-popup-close"
                    className="absolute top-3 right-3 p-1.5 rounded-md text-white/70 hover:bg-white/10 hover:text-white"
                >
                    <X className="w-4 h-4" />
                </button>
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        background:
                            "radial-gradient(circle at 20% 0%, rgba(57,200,193,0.4), transparent 50%), radial-gradient(circle at 100% 100%, rgba(214,176,107,0.3), transparent 50%)",
                    }}
                />
                <div className="relative p-7">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-teal/15 text-teal mb-3">
                        <Mail className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-bold leading-tight">{t("newsletter.title")}</h2>
                    <p className="mt-2 text-white/70 text-sm">{t("newsletter.subtitle")}</p>
                    <p className="mt-3 text-xs text-teal font-semibold uppercase tracking-wider">
                        {t("newsletter.social_proof")}
                    </p>
                    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="newsletter-popup-form">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("newsletter.placeholder")}
                            className="w-full px-4 py-3 rounded-md bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:border-teal outline-none text-sm"
                            data-testid="newsletter-popup-email"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-700 text-dark-navy font-semibold py-3 rounded-md disabled:opacity-70"
                            data-testid="newsletter-popup-submit"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {t("actions.subscribe")}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function shouldShow(frequency) {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return true;
        const data = JSON.parse(raw);
        if (frequency === "every_visit") return false; // shown this session
        if (frequency === "every_week") {
            const week = 7 * 24 * 60 * 60 * 1000;
            return Date.now() - (data.t || 0) > week;
        }
        return false; // "once"
    } catch (_) {
        return true;
    }
}

function markShown(frequency) {
    try {
        localStorage.setItem(KEY, JSON.stringify({ t: Date.now(), f: frequency }));
    } catch (_) {}
}
